/** Device-local drafts/outbox, never the production ledger. No PIN, cookie or approval token is stored. */
const databases = new Map<string, Promise<IDBDatabase>>();
export const operatorDatabaseName = (preview: boolean) => `anl-operator-local-v2:${preview ? "demo" : "live"}`;
export function openOperatorDatabase(preview: boolean): Promise<IDBDatabase> {
  const name = operatorDatabaseName(preview);
  const existing = databases.get(name);
  if (existing) return existing;
  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    let rejected = false;
    const request = indexedDB.open(name, 2);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("writes")) {
        const writes = db.createObjectStore("writes", { keyPath: "idempotencyKey" });
        writes.createIndex("owner", ["terminalCode", "operatorId"]);
      }
      if (!db.objectStoreNames.contains("drafts")) db.createObjectStore("drafts", { keyPath: "key" });
      if (!db.objectStoreNames.contains("criticalWrites")) {
        const criticalWrites = db.createObjectStore("criticalWrites", { keyPath: "idempotencyKey" });
        criticalWrites.createIndex("owner", ["terminalCode", "operatorId", "sessionId", "runId", "kind"]);
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      if (rejected) { db.close(); return; }
      db.onversionchange = () => { db.close(); databases.delete(name); };
      resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error("Cihaz deposu açılamadı."));
    request.onblocked = () => { rejected = true; reject(new Error("Cihaz deposu başka bir pencerede güncelleniyor. Diğer ANL penceresini kapatın.")); };
  });
  databases.set(name, opening);
  void opening.catch(() => databases.delete(name));
  return opening;
}
export function idbResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Cihaz kaydı işlenemedi."));
  });
}
/** Work may await IndexedDB requests only; no network/timers inside a transaction. */
export async function localTransaction<T>(preview: boolean, names: string[], mode: IDBTransactionMode,
  work: (transaction: IDBTransaction) => Promise<T>): Promise<T> {
  const db = await openOperatorDatabase(preview);
  const transaction = db.transaction(names, mode);
  const completed = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("Cihaz kaydı tamamlanamadı."));
    transaction.onerror = () => { /* Abort is the final outcome; request handlers retain the specific error. */ };
  });
  // Attach an immediate handler so an abort cannot become an unhandled rejection while work unwinds.
  void completed.catch(() => undefined);
  try {
    const value = await work(transaction);
    await completed;
    return value;
  } catch (error) {
    try { transaction.abort(); } catch { /* Already committed/aborted. */ }
    await completed.catch(() => undefined);
    throw error;
  }
}
export const localDatabaseChangedEvent = "anl-operator-local-changed";
export function notifyLocalDatabaseChanged() {
  window.dispatchEvent(new Event(localDatabaseChangedEvent));
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(localDatabaseChangedEvent);
    channel.postMessage("changed"); channel.close();
  }
}
